// route.ts - URL 경로 라우팅 (react-router 없음 - AGENTS.md).
//
// 화면 하나 = 주소 하나다. 링크로 보낸 화면이 받는 사람에게 그대로 열리고,
// 새로고침·뒤로가기가 화면 단위로 동작한다. 라우터를 들이지 않고 `pathname`
// 하나만 본다 - 경로 모양이 셋뿐이다.
//
//   /<탭>                 탭 (종합현황은 `/`)
//   /items/<id>           개발품 상세
//   /erp/<모듈>/<메뉴>    ERP 뷰 `<모듈>.<메뉴>` (뷰 키를 인코딩 없이 쓴다 -
//                         api `tests/erp/test_registry.py` 가 키 형식을 단언한다)
//
// `/admin` 은 탭이 아니라 별도 라우트다. 담당자용 앱과 운영자용 콘솔은 청중이
// 다르고, 담당자 화면의 메뉴에 운영 기능이 섞이면 안 된다.
//
// 조회 조건(기간·고객 …)은 주소에 담지 않는다 - 링크는 화면까지다
// (결정 2026-10-26, 딥링크 Option B).
import type { ScreenId } from "@/components/Shell";

/** 앱 본체 안의 위치. 해당 화면이 아닌 부가 정보(`itemId`/`erpView`)는 무시된다. */
export interface AppRoute {
  screen: ScreenId;
  itemId?: string;
  erpView?: string;
}

export type Route = ({ kind: "app" } & AppRoute) | { kind: "admin" };

export const ADMIN_PATH = "/admin";

/** 첫 경로 조각으로 열리는 탭. 슈퍼어드민은 `/admin` 이라 여기 없다. */
const TABS: readonly ScreenId[] = [
  "overview",
  "items",
  "schedule",
  "issues",
  "erp",
  "mytasks",
  "master",
  "feedback",
];

const ERP_SEGMENT = /^[a-z0-9_]+$/;

function decode(s: string): string | null {
  try {
    return decodeURIComponent(s);
  } catch {
    return null;
  }
}

/**
 * `pathname` → 라우트. 순수 함수 (테스트는 `route.test.ts`).
 *
 * 트레일링 슬래시와 탭·ERP 조각의 대소문자를 흡수한다 - `/ERP/Sales/` 도 같은
 * 화면이다. 개발품 id 는 대소문자를 보존한다. 알 수 없는 경로는 404 가 아니라
 * 종합현황이다 - 주소창은 `App.tsx` 가 정규 주소로 바로잡는다.
 */
export function parseRoute(pathname: string): Route {
  const parts = pathname.split("/").filter(Boolean);
  const head = (parts[0] ?? "overview").toLowerCase();
  const rest = parts.slice(1);
  if (head === ADMIN_PATH.slice(1) && rest.length === 0) return { kind: "admin" };
  const screen = TABS.find((t) => t === head);
  if (!screen) return { kind: "app", screen: "overview" };
  if (screen === "items" && rest.length === 1) {
    const itemId = decode(rest[0] ?? "");
    return itemId ? { kind: "app", screen, itemId } : { kind: "app", screen };
  }
  if (screen === "erp" && rest.length === 2) {
    const [m, v] = rest.map((s) => s.toLowerCase());
    if (m && v && ERP_SEGMENT.test(m) && ERP_SEGMENT.test(v)) {
      return { kind: "app", screen, erpView: `${m}.${v}` };
    }
    return { kind: "app", screen };
  }
  // 탭 뒤에 붙은 모르는 조각은 버린다 - 탭까지는 맞게 연다.
  return { kind: "app", screen };
}

/** 라우트 → 정규 주소. `parseRoute` 와 왕복한다. */
export function pathFor(r: AppRoute): string {
  if (r.screen === "overview") return "/";
  if (r.screen === "superadmin") return ADMIN_PATH;
  if (r.screen === "items" && r.itemId) return `/items/${encodeURIComponent(r.itemId)}`;
  if (r.screen === "erp" && r.erpView) {
    const [m, v, ...extra] = r.erpView.split(".");
    if (m && v && extra.length === 0 && ERP_SEGMENT.test(m) && ERP_SEGMENT.test(v)) {
      return `/erp/${m}/${v}`;
    }
  }
  return `/${r.screen}`;
}

/**
 * 경로 이동. `pushState` 는 `popstate` 를 쏘지 않으므로 직접 쏜다 -
 * 그래야 뒤로가기와 앱 내 이동이 같은 구독자를 지난다(경로 상태의 단일 소스).
 *
 * `replace` 는 기록을 쌓지 않는다 - 주소를 바로잡을 때(잘못된 경로, ERP 의
 * 기본 메뉴 선택)는 뒤로가기가 같은 화면에 갇히지 않게 한다.
 */
export function navigateTo(path: string, opts: { replace?: boolean } = {}): void {
  if (window.location.pathname === path) return;
  if (opts.replace) window.history.replaceState(null, "", path);
  else window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * `<a href>` 클릭을 앱 안에서 처리해도 되는가. 보조키·가운데 버튼은 새 탭·새
 * 창으로 여는 브라우저 동작이라 가로채지 않는다.
 */
export function isPlainClick(
  e: Pick<MouseEvent, "button" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey" | "defaultPrevented">
): boolean {
  return (
    !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
  );
}
